import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import SEO from '../../components/SEO';
import { getFullUrl } from '../../utils/constants';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import AuthTabs from './components/AuthTabs';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import LoginForm from './components/LoginForm';
import RegistrationForm from './components/RegistrationForm';
import OTPVerificationModal from './components/OTPVerificationModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';

const AuthenticationHub: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, locale } = useTranslation();
  const { platformName, platformDescription, systemLogo, systemLogoDark, showSystemName, authenticationHubImage } = useSettings();
  const { effectiveTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationPhone, setVerificationPhone] = useState('');
  const [verificationType, setVerificationType] = useState<'email' | 'phone' | 'both'>('email');

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/timeline', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleRegistrationSuccess = (data: { email?: string; phone?: string; otpType?: string }) => {
    // Handle both email and phone verification
    if (data.email && data.phone && data.otpType === 'both') {
      setVerificationEmail(data.email);
      setVerificationPhone(data.phone);
      setVerificationType('both');
    } else if (data.email) {
      setVerificationEmail(data.email);
      setVerificationType('email');
    } else if (data.phone) {
      setVerificationPhone(data.phone);
      setVerificationType('phone');
    }
    setShowEmailVerificationModal(true);
  };

  const features = [
    { icon: 'MessageSquare', textKey: 'auth.feature.discussions' },
    { icon: 'Calendar', textKey: 'auth.feature.events' },
    { icon: 'Award', textKey: 'auth.feature.badges' },
    { icon: 'Users', textKey: 'auth.feature.connect' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Login - Sisi ni Tanzania"
        description="Sign in to Sisi ni Tanzania to access forums, events, opportunities, and connect with Tanzania's community."
        keywords="login, sign in, Sisi ni Tanzania, Tanzania platform, account access"
        url={getFullUrl("/login")}
        noindex={true}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="outline" size="sm" showLabel />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
          <div className="order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6">
              {(systemLogo || systemLogoDark) ? (
                <Link to="/" className="inline-flex items-center justify-center hover:opacity-80 transition-opacity">
                  <Image
                    src={effectiveTheme === 'dark' && systemLogoDark ? systemLogoDark : (systemLogo || '')}
                    alt={platformName}
                    className="w-auto h-auto max-w-none object-contain"
                  />
                </Link>
              ) : (
                <Link to="/" className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity">
                  <Icon name="Users" size={28} className="text-primary-foreground" />
                </Link>
              )}
              {showSystemName && (
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{platformName}</h1>
                  <p className="text-sm text-muted-foreground">{platformDescription || t('auth.subtitle')}</p>
                </div>
              )}
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('auth.welcome_title')}</h2>
            <p className="text-muted-foreground mb-8">{t('auth.description')}</p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) =>
              <div key={index} className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={feature.icon} size={20} className="text-primary" />
                  </div>
                  <p className="text-sm text-foreground font-medium">{t(feature.textKey)}</p>
                </div>
              )}
            </div>

            <div className="hidden lg:block">
              <div className="relative h-64 rounded-lg overflow-hidden elevation-md">
                <Image
                  src={authenticationHubImage || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600'%3E%3Crect fill='%23e5e7eb' width='800' height='600'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EImage Coming Soon%3C/text%3E%3C/svg%3E"}
                  alt="Diverse group of young African professionals collaborating around laptop in modern office space with natural lighting"
                  className="w-full h-full object-cover" />

              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="bg-card rounded-lg p-8 elevation-lg border border-border">
              <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

              {activeTab === 'login' ?
              <LoginForm 
                onForgotPassword={() => setShowForgotPassword(true)}
                onEmailVerificationRequired={(email) => {
                  setVerificationEmail(email);
                  setShowEmailVerificationModal(true);
                }}
              /> :

              <RegistrationForm language={locale as 'en' | 'sw'} onSuccess={handleRegistrationSuccess} />
              }

            </div>

            <div className="mt-6 text-center">
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <a href="/privacy" className="hover:text-foreground transition-fast">
                  {t('auth.privacy_policy')}
                </a>
                <span>•</span>
                <a href="/terms" className="hover:text-foreground transition-fast">
                  {t('auth.terms_of_service')}
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                © {new Date().getFullYear()} Sisi ni Tanzania. {t('auth.copyright')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showEmailVerificationModal &&
      <OTPVerificationModal
        identifier={verificationEmail || verificationPhone}
        type={verificationType}
        language={locale as 'en' | 'sw'}
        onClose={() => {
          setShowEmailVerificationModal(false);
          setVerificationEmail('');
          setVerificationPhone('');
          setVerificationType('email');
        }}
        skipAutoSend={true}
        emailAddress={verificationEmail}
        phoneNumber={verificationPhone}
      />
      }

      {showForgotPassword && <ForgotPasswordModal language={locale as 'en' | 'sw'} onClose={() => setShowForgotPassword(false)} />}
    </div>);

};

export default AuthenticationHub;